import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { useUser } from '@clerk/clerk-react';
import { useCallback } from 'react';
import { toast } from 'sonner';
import { chatService, type Message, type MessageInsert } from '@/lib/api/services/chat.service';
import { useSubscribeToMessages } from '@/lib/websocket/realtime-chat';

export function useMessages(conversationId: string | null) {
  const { user } = useUser();
  const queryClient = useQueryClient();
  const queryKey = ['messages', conversationId];

  // Fetch messages with infinite scroll
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
  } = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam = 0 }) => {
      if (!conversationId) return { messages: [], nextOffset: null };
      const limit = 50;
      const result = await chatService.getMessages(conversationId, limit, pageParam);
      return {
        messages: result.messages,
        nextOffset: result.pagination.has_more ? pageParam + limit : null,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextOffset,
    enabled: !!conversationId && !!user?.id,
    initialPageParam: 0,
    staleTime: 30000, // 30 seconds
    refetchOnWindowFocus: false,
    refetchInterval: (query) => {
      // Fallback to polling if WebSocket fails (check if error occurred)
      const hasError = query.state.error;
      if (hasError && !document.hidden) {
        return 10000; // Poll every 10 seconds as fallback
      }
      return false; // Use WebSocket for real-time updates
    },
  });

  // Flatten messages from all pages and sort by created_at (oldest first)
  const messages = (data?.pages.flatMap((page) => page.messages) || []).sort(
    (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
  );

  // Subscribe to new messages via WebSocket
  useSubscribeToMessages(
    conversationId,
    (message: Message) => {
      // Update React Query cache in real-time
      queryClient.setQueryData(queryKey, (old: any) => {
        if (!old) {
          // If no old data, create new structure
          return {
            pages: [
              {
                messages: [message],
                nextOffset: null,
              },
            ],
            pageParams: [0],
          };
        }

        // Check if message already exists (avoid duplicates)
        // Also check for optimistic messages that should be replaced
        const existingMessage = old.pages
          .flatMap((page: any) => page.messages)
          .find((m: Message) => m.id === message.id);

        if (existingMessage) {
          // Update existing message (use new message's read status)
          return {
            ...old,
            pages: old.pages.map((page: any) => ({
              ...page,
              messages: page.messages.map((m: Message) =>
                m.id === message.id ? message : m
              ),
            })),
          };
        }

        // Check if this is a real message replacing an optimistic one
        // (same sender, same content/file, within a few seconds)
        const now = new Date(message.created_at).getTime();
        const optimisticToReplace = old.pages
          .flatMap((page: any) => page.messages)
          .find((m: Message) => {
            if (!m.id.startsWith('temp-')) return false;
            if (m.sender_id !== message.sender_id) return false;
            
            // Match by content for text messages or file_url for file messages
            const contentMatch = m.message_type === 'text' 
              ? m.content === message.content
              : m.file_url === message.file_url;
            
            if (!contentMatch) return false;
            
            const optimisticTime = new Date(m.created_at).getTime();
            return Math.abs(now - optimisticTime) < 10000; // Within 10 seconds
          });

        // Add new message to the first page and maintain chronological order
        const firstPage = old.pages[0] || { messages: [], nextOffset: null };
        let messagesToUpdate = [...firstPage.messages];
        
        // Replace optimistic message if found
        if (optimisticToReplace) {
          messagesToUpdate = messagesToUpdate.filter((m: Message) => m.id !== optimisticToReplace.id);
        }
        
        // Add real message and sort chronologically
        const updatedMessages = [...messagesToUpdate, message].sort(
          (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        );

        return {
          ...old,
          pages: [
            {
              ...firstPage,
              messages: updatedMessages,
            },
            ...old.pages.slice(1),
          ],
        };
      });

      // Invalidate conversations to update last message preview
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
    () => {
      // Invalidate query to trigger refetch with polling fallback
      queryClient.invalidateQueries({ queryKey });
    }
  );

  // Send message mutation - optimized for smooth UX
  const sendMessageMutation = useMutation({
    mutationFn: async ({ messageData }: { messageData: MessageInsert }) => {
      if (!conversationId) throw new Error('Conversation ID is required');
      return await chatService.sendMessage(conversationId, messageData);
    },
    onMutate: async ({ messageData }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey });

      // Snapshot previous value
      const previousData = queryClient.getQueryData(queryKey);

      // Create optimistic message with unique temp ID
      const tempId = `temp-${Date.now()}-${Math.random()}`;
      const optimisticMessage: Message & { is_read: boolean; read_at: string | null } = {
        id: tempId,
        conversation_id: conversationId!,
        sender_id: user!.id,
        content: messageData.content || '',
        message_type: messageData.message_type || 'text',
        file_url: messageData.file_url || null,
        file_name: messageData.file_name || null,
        file_type: messageData.file_type || null,
        caption: messageData.caption || null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        deleted_at: null,
        is_read: false, // Not read by recipient yet
        read_at: null,
      };

      // Update cache optimistically - add to first page
      queryClient.setQueryData(queryKey, (old: any) => {
        if (!old) {
          // If no old data, create new structure
          return {
            pages: [
              {
                messages: [optimisticMessage],
                nextOffset: null,
              },
            ],
            pageParams: [0],
          };
        }

        const firstPage = old.pages[0] || { messages: [], nextOffset: null };
        
        // Add optimistic message and sort chronologically
        const updatedMessages = [...firstPage.messages, optimisticMessage].sort(
          (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        );

        return {
          ...old,
          pages: [
            {
              ...firstPage,
              messages: updatedMessages,
            },
            ...old.pages.slice(1),
          ],
        };
      });

      // Invalidate conversations to update last message preview
      queryClient.invalidateQueries({ queryKey: ['conversations'] });

      return { previousData, optimisticMessage, tempId };
    },
    onError: (error, _variables, context) => {
      // Rollback on error
      if (context?.previousData) {
        queryClient.setQueryData(queryKey, context.previousData);
      }
      toast.error('Failed to send message');
      console.error('Error sending message:', error);
      // Throw error so MessageInput can handle it
      throw error;
    },
    onSuccess: (data, _variables, context) => {
      // Replace optimistic message with real data when API responds
      queryClient.setQueryData(queryKey, (old: any) => {
        if (!old) return old;
        
        // Check if real message already exists (from WebSocket event)
        const existingRealMessage = old.pages
          .flatMap((page: any) => page.messages)
          .find((m: Message) => m.id === data.id);

        if (existingRealMessage) {
          // Real message already exists via WebSocket, just remove optimistic one
          return {
            pages: old.pages.map((page: any) => ({
              ...page,
              messages: page.messages.filter(
                (m: Message) => m.id !== context?.optimisticMessage.id
              ),
            })),
            pageParams: old.pageParams,
          };
        }

        // Replace optimistic message with real one (use backend's read status)
        return {
          pages: old.pages.map((page: any) => ({
            ...page,
            messages: page.messages.map((m: Message) =>
              m.id === context?.optimisticMessage.id
                ? data
                : m
            ),
          })),
          pageParams: old.pageParams,
        };
      });

      // Invalidate conversations to update last message preview
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
  });

  // Mark as read mutation
  const markAsReadMutation = useMutation({
    mutationFn: async (messageId: string) => {
      await chatService.markAsRead(messageId);
      return messageId;
    },
    onSuccess: (_data, messageId) => {
      // Optimistically update read status
      queryClient.setQueryData(queryKey, (old: any) => {
        if (!old) return old;
        return {
          ...old,
          pages: old.pages.map((page: any) => ({
            ...page,
            messages: page.messages.map((m: Message) =>
              m.id === messageId
                ? { ...m, is_read: true, read_at: new Date().toISOString() }
                : m
            ),
          })),
        };
      });
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
  });

  // Mark multiple messages as read mutation
  const markMultipleAsReadMutation = useMutation({
    mutationFn: async (messageIds: string[]) => {
      await chatService.markMultipleAsRead(messageIds);
    },
    onSuccess: (_, messageIds) => {
      // Optimistically update read status
      queryClient.setQueryData(queryKey, (old: any) => {
        if (!old) return old;
        const now = new Date().toISOString();
        return {
          ...old,
          pages: old.pages.map((page: any) => ({
            ...page,
            messages: page.messages.map((m: Message) =>
              messageIds.includes(m.id)
                ? { ...m, is_read: true, read_at: now }
                : m
            ),
          })),
        };
      });
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
  });

  // Delete message mutation
  const deleteMessageMutation = useMutation({
    mutationFn: async (messageId: string) => {
      await chatService.deleteMessage(messageId);
      return messageId;
    },
    onMutate: async (messageId) => {
      await queryClient.cancelQueries({ queryKey });

      const previousData = queryClient.getQueryData(queryKey);

      // Optimistically remove message
      queryClient.setQueryData(queryKey, (old: any) => {
        if (!old) return old;
        return {
          pages: old.pages.map((page: any) => ({
            ...page,
            messages: page.messages.filter((m: Message) => m.id !== messageId),
          })),
          pageParams: old.pageParams,
        };
      });

      return { previousData };
    },
    onError: (error, _messageId, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(queryKey, context.previousData);
      }
      toast.error('Failed to delete message');
      console.error('Error deleting message:', error);
    },
    onSuccess: () => {
      toast.success('Message deleted');
    },
  });

  const sendMessage = useCallback(
    (messageData: MessageInsert) => {
      sendMessageMutation.mutate({ messageData });
    },
    [sendMessageMutation]
  );

  return {
    messages,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    sendMessage,
    markAsRead: markAsReadMutation.mutateAsync,
    markMultipleAsRead: markMultipleAsReadMutation.mutateAsync,
    deleteMessage: deleteMessageMutation.mutateAsync,
    isSending: sendMessageMutation.isPending,
    isDeleting: deleteMessageMutation.isPending,
  };
}

export function useMessageSearch(conversationId: string | null, searchQuery: string) {
  const { user } = useUser();
  const queryKey = ['message-search', conversationId, searchQuery];

  const {
    data: searchResults = [],
    isLoading,
    error,
  } = useQuery({
    queryKey,
    queryFn: async () => {
      if (!conversationId || !searchQuery.trim()) return [];
      return await chatService.searchMessages(conversationId, searchQuery.trim());
    },
    enabled: !!conversationId && !!searchQuery.trim() && !!user?.id,
  });

  return {
    searchResults,
    isLoading,
    error,
  };
}
